<script setup lang="ts">
import { ref, computed, onBeforeUnmount, watch } from 'vue'

interface TimelapseSample {
    imageUrl: string
    time: Date
    growthPercent?: number
    managed?: boolean
}

const props = withDefaults(defineProps<{
    samples: TimelapseSample[]
    intervalMs?: number
    height?: string
}>(), {
    intervalMs: 1500,
    height: '450px'
})

const currentIndex = ref(0)
const playing = ref(false)
const speed = ref(props.intervalMs)
let timer: number | null = null

// Samples con imagen, ordenadas cronológicamente (antigua → reciente)
const timelapseFrames = computed(() => {
    return [...props.samples]
        .filter(s => s.imageUrl && s.imageUrl.trim() !== '')
        .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime())
})

const currentFrame = computed(() => timelapseFrames.value[currentIndex.value] || null)

const progress = computed(() => {
    if (timelapseFrames.value.length <= 1) return 100
    return Math.round((currentIndex.value / (timelapseFrames.value.length - 1)) * 100)
})

const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('es-CL', {
        day: '2-digit', month: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit'
    }).format(new Date(date))
}

const play = () => {
    if (timelapseFrames.value.length <= 1) return
    playing.value = true
    timer = window.setInterval(() => {
        if (currentIndex.value >= timelapseFrames.value.length - 1) {
            currentIndex.value = 0
        } else {
            currentIndex.value++
        }
    }, speed.value)
}

const pause = () => {
    playing.value = false
    if (timer) {
        clearInterval(timer)
        timer = null
    }
}

const togglePlay = () => {
    if (playing.value) {
        pause()
    } else {
        play()
    }
}

const goTo = (index: number) => {
    pause()
    currentIndex.value = index
}

const prev = () => {
    pause()
    currentIndex.value = currentIndex.value > 0
        ? currentIndex.value - 1
        : timelapseFrames.value.length - 1
}

const next = () => {
    pause()
    currentIndex.value = currentIndex.value < timelapseFrames.value.length - 1
        ? currentIndex.value + 1
        : 0
}

const changeSpeed = (ms: number) => {
    speed.value = ms
    if (playing.value) {
        pause()
        play()
    }
}

// Restart al cambiar samples
watch(() => props.samples, () => {
    pause()
    currentIndex.value = 0
})

onBeforeUnmount(() => {
    pause()
})
</script>

<template>
    <div v-if="timelapseFrames.length === 0" class="text-center pa-8">
        <v-icon size="48" color="grey-lighten-2">mdi-image-off</v-icon>
        <div class="text-body-2 text-medium-emphasis mt-2">Sin imágenes disponibles para timelapse</div>
    </div>

    <div v-else>
        <!-- Visor de imagen -->
        <div class="position-relative" :style="{ height: height }">
            <v-img
                v-if="currentFrame"
                :src="currentFrame.imageUrl"
                :height="height"
                cover
                class="rounded-lg timelapse-img"
            >
                <template v-slot:placeholder>
                    <v-row class="fill-height ma-0" align="center" justify="center">
                        <v-progress-circular indeterminate color="white"></v-progress-circular>
                    </v-row>
                </template>
            </v-img>

            <!-- Overlay superior: fecha y datos -->
            <div class="timelapse-overlay-top">
                <v-chip color="black" variant="flat" size="small" class="mr-2">
                    <v-icon size="14" class="mr-1">mdi-clock-outline</v-icon>
                    {{ formatDate(currentFrame!.time) }}
                </v-chip>
                <v-chip v-if="currentFrame?.managed && currentFrame?.growthPercent != null"
                    color="success" variant="flat" size="small">
                    <v-icon size="14" class="mr-1">mdi-trending-up</v-icon>
                    {{ currentFrame.growthPercent }}%
                </v-chip>
            </div>

            <!-- Overlay inferior: contador -->
            <div class="timelapse-overlay-bottom">
                <v-chip color="black" variant="flat" size="x-small">
                    {{ currentIndex + 1 }} / {{ timelapseFrames.length }}
                </v-chip>
            </div>

            <!-- Botón abrir en nueva pestaña -->
            <v-btn v-if="currentFrame" icon size="small" color="white" class="position-absolute"
                style="top: 8px; right: 8px;" :href="currentFrame.imageUrl" target="_blank">
                <v-icon size="18">mdi-open-in-new</v-icon>
                <v-tooltip activator="parent" location="top">Abrir imagen</v-tooltip>
            </v-btn>
        </div>

        <!-- Controles de reproducción -->
        <div class="d-flex align-center mt-3 px-2">
            <v-btn icon size="small" variant="text" @click="prev">
                <v-icon>mdi-skip-previous</v-icon>
            </v-btn>

            <v-btn icon size="small" :color="playing ? 'error' : 'primary'" variant="tonal" @click="togglePlay" class="mx-1">
                <v-icon>{{ playing ? 'mdi-pause' : 'mdi-play' }}</v-icon>
            </v-btn>

            <v-btn icon size="small" variant="text" @click="next">
                <v-icon>mdi-skip-next</v-icon>
            </v-btn>

            <!-- Slider de posición -->
            <v-slider
                v-model="currentIndex"
                :max="timelapseFrames.length - 1"
                :min="0"
                step="1"
                hide-details
                density="compact"
                color="primary"
                class="mx-3"
                @update:model-value="goTo($event)"
            ></v-slider>

            <!-- Velocidad -->
            <v-btn-toggle v-model="speed" mandatory density="compact" variant="outlined" divided>
                <v-btn :value="2500" size="x-small" @click="changeSpeed(2500)">
                    0.5x
                </v-btn>
                <v-btn :value="1500" size="x-small" @click="changeSpeed(1500)">
                    1x
                </v-btn>
                <v-btn :value="700" size="x-small" @click="changeSpeed(700)">
                    2x
                </v-btn>
                <v-btn :value="300" size="x-small" @click="changeSpeed(300)">
                    4x
                </v-btn>
            </v-btn-toggle>
        </div>

        <!-- Barra de progreso -->
        <v-progress-linear
            :model-value="progress"
            height="3"
            color="primary"
            class="mt-1"
            rounded
        ></v-progress-linear>

        <!-- Timeline de thumbnails -->
        <div v-if="timelapseFrames.length <= 20" class="d-flex mt-2 overflow-x-auto" style="gap: 4px;">
            <div
                v-for="(frame, index) in timelapseFrames"
                :key="index"
                class="timelapse-thumb"
                :class="{ 'timelapse-thumb-active': index === currentIndex }"
                @click="goTo(index)"
            >
                <v-img :src="frame.imageUrl" width="48" height="36" cover class="rounded"></v-img>
            </div>
        </div>
    </div>
</template>

<style scoped>
.timelapse-img {
    transition: opacity 0.3s ease;
}

.timelapse-overlay-top {
    position: absolute;
    top: 8px;
    left: 8px;
    z-index: 1;
    display: flex;
    align-items: center;
}

.timelapse-overlay-bottom {
    position: absolute;
    bottom: 8px;
    left: 8px;
    z-index: 1;
}

.timelapse-thumb {
    cursor: pointer;
    border: 2px solid transparent;
    border-radius: 6px;
    overflow: hidden;
    opacity: 0.5;
    transition: all 0.2s ease;
    flex-shrink: 0;
}

.timelapse-thumb:hover {
    opacity: 0.8;
}

.timelapse-thumb-active {
    border-color: rgb(var(--v-theme-primary));
    opacity: 1;
}
</style>
